import React, { useMemo, useCallback, useState, useEffect } from "react";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Card, CardBody, Button, Container } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { generateColumns } from "../../../custom/Utils/ColumnUtils";
import useFetchData from "../../../custom/Utils/UseFetchDataUtils";
import usePagination from "../../../custom/Utils/UsePaginationUtils";
import { listPageData, exportPageData } from "./ItemFile";
import {
  apiUrl,
  baseURL,
  getRecords,
  uploadExelFile,
  uploadFile,
} from "../../../services/api.service";
import { toast } from "react-toastify";
import { LoaderCreatePage } from "../../Loader/Loader";
import { loaderOverlay } from "../../../Style/loaderOverly";
import ModernPopup from "../../../services/popup/Popup";

const ItemList = () => {
  const navigate = useNavigate();
  const [itemSelect, setItemSelect] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const {
    payload,
    navigatePage,
    headerName,
    columnData,
    endpoint,
    searchfields,
    exportFileName,
  } = listPageData(itemSelect?.value);
  const initialPayload = useMemo(() => payload, [itemSelect, selectedFile]);

  // pagination
  const {
    page,
    pageSize,
    nextPage,
    previousPage,
    lastPage,
    firstPage,
    handlePageSizeChange,
    setPage,
  } = usePagination();

  // fetch data using the provided payload, page, and page size
  const { data, isLoading, setSearchKeyword, totalCount } = useFetchData(
    initialPayload,
    page,
    pageSize,
    endpoint,
    searchfields
  );
  const [dataForExport, setDataForExport] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [popupData, setPopupData] = useState({
    isOpen: false,
    message: "",
    isError: false,
  });
  const [costOption, setCostOption] = useState(false);

  // handle create action
  const handleCreate = () => navigate(navigatePage.create);

  // handle edit action
  const handleEdit = useCallback(
    (id) => {
      navigate(navigatePage.edit + "/" + id);
    },
    [navigate, navigatePage.edit]
  );
  const handleView = useCallback(
    (id) => {
      navigate(navigatePage.view + "/" + id);
    },
    [navigate, navigatePage.view]
  );
  useEffect(() => {
    const userDetails = JSON.parse(localStorage.getItem("userDetails") || {});
    const role = userDetails?.role_masters?.role_name;
    if (role?.toLowerCase() === "cost user".toLowerCase()) {
      setCostOption(true);
    } else {
      setCostOption(false);
    }
  }, []);
  // columns data
  let modifiedColumnData = columnData;
  if (!costOption) {
    modifiedColumnData = columnData.filter((col) => col.value !== "cost");
  }

  const columns = useMemo(
    () =>
      generateColumns(
        data,
        modifiedColumnData,
        handleEdit,
        handleView,
        page,
        pageSize,
        "itemList"
      ),
    [data, columnData, handleEdit, handleView, page, pageSize]
  );

  // handle page size change callback
  const handlePageSizeChangeWrapper = useCallback(
    (newSize) => {
      if (newSize !== pageSize) {
        handlePageSizeChange(newSize, totalCount);
        setPage(0); // Reset to first page when page size changes
      }
    },
    [handlePageSizeChange, pageSize, totalCount, setPage]
  );
  useEffect(() => {
    const handleExportData = async () => {
      try {
        const responseData = await getRecords(initialPayload, endpoint);
        setDataForExport(responseData.data);
      } catch (error) {
        console.error("Error fetching records:", error);
      }
    };

    handleExportData();
  }, [itemSelect]);

  const exportData = dataForExport?.map((item, index) => ({
    "Sr. No": index + 1,
    "ITEM CODE": item?.item_code || "N/A",
    DESCRIPTION: item?.description || "N/A",
    UOM: item?.uom?.uom_name || "N/A",
    ...(costOption && { COST: item.cost || "N/A" }),
    TYPE: item?.type == 1 ? "Make" : item?.type == 2 ? "Buy" : "N/A",
    ORIGIN: item?.origin == 1 ? "Bermad" : item?.origin == 2 ? "Local" : "N/A",
    CATEGORY: item?.category?.category_name || "N/A",

    "CREATED BY": item?.createdBy?.first_name || "N/A",
    STATUS:
      item?.is_active === false || item?.is_active === 0
        ? "In-active"
        : "Active",
  }));

  const handleFileUpload = async (e) => {
    try {
      const file = e.target.files[0];

      const fileExtension = file.name.split(".").pop().toLowerCase();

      if (fileExtension !== "xlsx" && fileExtension !== "xls") {
        throw new Error("Please select a valid Excel file.");
      }

      setIsLoadingData(true);
      const userDetails = JSON.parse(localStorage.getItem("userDetails")) || {};
      const createdBy = userDetails.id || null;

      const res = await uploadExelFile(
        "uploads",
        file,
        `/importItems?createdBy=${createdBy}`
      );
      setIsLoadingData(false);

      if (!res?.code && res?.message) {
        setPopupData({
          isOpen: true,
          message: res.message || "File imported successfully!",
          isError: false,
        });
        setSelectedFile(file);
      } else {
        setPopupData({
          isOpen: true,
          message: res?.message || "Error importing file. Please try again.",
          isError: true,
        });
      }
    } catch (error) {
      console.log("Upload error:", error);
      setIsLoadingData(false);

      const errorMessage =
        error?.message ||
        error?.Error ||
        "An error occurred while importing. Please try again.";

      setPopupData({
        isOpen: true,
        message: errorMessage,
        isError: true,
      });
    }

    setTimeout(() => {
      e.target.value = "";
    }, 0);
  };
  const closePopup = () => {
    setPopupData((prev) => ({ ...prev, isOpen: false }));
  };
  return (
    <React.Fragment>
      <div className="page-content">
        <ModernPopup
          isOpen={popupData.isOpen}
          onClose={closePopup}
          message={popupData.message}
          isError={popupData.isError}
        />
        <Container fluid>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <Breadcrumbs title={headerName + " List"} breadcrumbItems={[]} />
            <Button
              color="primary"
              className="waves-effect waves-light me-1"
              onClick={handleCreate}
              style={{ marginLeft: "auto", background: "#006eb9" }}
            >
              Create {headerName}
            </Button>
            <Button
              color="success"
              className="waves-effect waves-light me-1"
              onClick={() => document.getElementById("fileInput").click()}
            >
              Import
            </Button>
            <input
              type="file"
              id="fileInput"
              style={{ display: "none" }}
              accept=".xlsx, .xls"
              onChange={(e) => handleFileUpload(e)}
            />
          </div>

          <Card>
            <CardBody>
              <TableContainer
                columns={columns || []}
                data={data || []}
                isLoading={isLoading}
                onSearchInputData={setSearchKeyword}
                pageIndexProp={page}
                onPageSizeChange={handlePageSizeChangeWrapper}
                nextToPage={nextPage}
                lastToPage={() => lastPage(totalCount)}
                previousToPage={previousPage}
                previousFirstToPage={firstPage}
                totalCount={totalCount}
                customPageSize={pageSize}
                setItemSelect={setItemSelect}
                itemSelect={itemSelect}
                exportData={exportData}
                exportFileName={exportFileName}
              />
            </CardBody>
          </Card>
          {isLoadingData && (
            <div style={loaderOverlay}>
              <LoaderCreatePage />
            </div>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ItemList;
